import { useEffect, useState } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { HiOutlineTrophy, HiOutlineCalendar, HiOutlineBuildingLibrary, HiOutlineBuildingOffice2, HiOutlineChatBubbleLeftEllipsis, HiOutlineSwatch, HiOutlineSquares2X2 } from 'react-icons/hi2'
import { hasMetricsConsent } from "./common/CookieConsentBanner"

const API_URL = import.meta.env.VITE_API_URL

function PaintingDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const [painting, setPainting] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [imgIdx, setImgIdx] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [thumbnailStartIdx, setThumbnailStartIdx] = useState(0)

  // Neue States für Farben, Ausstellungen, Awards
  const [colors, setColors] = useState([])
  const [exhibitions, setExhibitions] = useState([])
  const [awards, setAwards] = useState([])
  const [series, setSeries] = useState([])

  useEffect(() => {
    fetch(`${API_URL}/colors`).then(res => res.ok ? res.json() : []).then(setColors)
    fetch(`${API_URL}/exhibitions`).then(res => res.ok ? res.json() : []).then(setExhibitions)
    fetch(`${API_URL}/awards`).then(res => res.ok ? res.json() : []).then(setAwards)
    fetch(`${API_URL}/series`).then(res => res.ok ? res.json() : []).then(setSeries)
  }, [])

  useEffect(() => {
    fetch(`${API_URL}/paintings/${id}`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
        return res.json()
      })
      .then(data => {
        setPainting(data)
        const images = data.images || [];
        // Robust main image selection
        const mainImgIdx = Array.isArray(images)
          ? images.findIndex(img => img.is_main === 1 || img.is_main === '1' || img.is_main === true)
          : -1;
        const displayImgIdx = mainImgIdx >= 0 ? mainImgIdx : 0;
        setImgIdx(displayImgIdx);
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }, [id])

  // Tracking: Painting View (nur nach erfolgreichem Laden und nur einmal)
  useEffect(() => {
    if (hasMetricsConsent() && painting && painting.id) {
      fetch(`${API_URL}/track-view`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Metrics-Consent": "true"
        },
        body: JSON.stringify({ entity_type: "painting", entity_id: Number(painting.id) })
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [painting?.id]);

  if (loading) return (
    <main className="flex-1 w-screen min-h-[400px] flex items-center justify-center bg-gray-100 dark:bg-zinc-900">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 dark:border-gray-100"></div>
    </main>
  )

  if (error) return (
    <main className="flex-1 w-screen min-h-[400px] flex items-center justify-center bg-gray-100 dark:bg-zinc-900">
      <div className="text-center text-red-500 dark:text-red-400 p-8">
        {error}
      </div>
    </main>
  )

  if (!painting) return null

  const images = painting.images || []

  // Hilfsfunktionen für Mapping
  const colorObjs = Array.isArray(painting.color_ids) && Array.isArray(colors)
    ? painting.color_ids.map(cid => (Array.isArray(colors) ? colors.find(c => c.id === cid) : null)).filter(Boolean)
    : [];

  const exhibitionObjs = Array.isArray(painting.exhibition_ids) && Array.isArray(exhibitions)
    ? painting.exhibition_ids.map(eid => (Array.isArray(exhibitions) ? exhibitions.find(e => e.id === eid) : null)).filter(Boolean)
      .sort((a, b) => {
        // Sort by end_date DESC, then start_date DESC (newest first)
        const endDateA = a.end_date ? new Date(a.end_date) : new Date(0);
        const endDateB = b.end_date ? new Date(b.end_date) : new Date(0);
        if (endDateA.getTime() !== endDateB.getTime()) {
          return endDateB - endDateA;
        }
        const startDateA = a.start_date ? new Date(a.start_date) : new Date(0);
        const startDateB = b.start_date ? new Date(b.start_date) : new Date(0);
        return startDateB - startDateA;
      })
    : [];

  const awardObjs = Array.isArray(painting.award_ids) && Array.isArray(awards)
    ? painting.award_ids.map(aid => (Array.isArray(awards) ? awards.find(a => a.id === aid) : null)).filter(Boolean)
      .sort((a, b) => {
        // Sort by date DESC (newest first)
        const dateA = a.date ? new Date(a.date) : new Date(0);
        const dateB = b.date ? new Date(b.date) : new Date(0);
        return dateB - dateA;
      })
    : [];

  const serieObj = Array.isArray(series) && painting.serie_id
    ? series.find(s => s.id === painting.serie_id)
    : null;

  // Hilfsfunktion für Datumsformatierung
  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  }

  // Hilfsfunktion für Monat + Jahr
  function formatMonthYear(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', { year: 'numeric', month: 'long' });
  }

  return (
    <div className="min-h-screen w-full bg-gray-100 dark:bg-zinc-900 transition-colors duration-300 flex flex-col">
      {/* Hero Image with Overlay */}
      <section className="relative w-screen flex flex-col items-center justify-center bg-gray-100 dark:bg-black pt-4 pb-2">
        {images.length > 0 && (
          <img
            src={`${API_URL}${images[imgIdx].file_path}`}
            alt={painting.name}
            className="w-full max-w-2xl h-auto max-h-[60vh] object-contain object-center bg-white dark:bg-black cursor-pointer mx-auto"
            style={{ display: 'block' }}
            onClick={() => setIsModalOpen(true)}
          />
        )}
      </section>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex justify-center mt-3 mb-6 md:mt-4 md:mb-8 w-full max-w-full gap-1 md:gap-2">
          <div className="flex items-center gap-2">
            {thumbnailStartIdx > 0 && (
              <button
                onClick={() => setThumbnailStartIdx(prev => Math.max(0, prev - 1))}
                className="p-2 rounded-full bg-white dark:bg-zinc-800 border border-gray-300 dark:border-zinc-700 hover:bg-gray-100 dark:hover:bg-zinc-700"
                aria-label="Vorherige Thumbnails"
              >
                ‹
              </button>
            )}
            <div className="flex gap-0.5 md:gap-2">
              {images.slice(thumbnailStartIdx, thumbnailStartIdx + 5).map((img, idx) => (
                <button
                  key={thumbnailStartIdx + idx}
                  onClick={() => setImgIdx(thumbnailStartIdx + idx)}
                  className={`aspect-[4/3] w-16 md:w-20 rounded border-2 overflow-hidden focus:outline-none transition-all ${imgIdx === thumbnailStartIdx + idx ? 'border-black dark:border-white scale-105' : 'border-gray-300 dark:border-zinc-600 opacity-70 hover:opacity-100'} bg-white dark:bg-black`}
                  aria-label={`Bild ${thumbnailStartIdx + idx + 1}`}
                >
                  <img
                    src={`${API_URL}${img.thumb_path || img.file_path}`}
                    alt={`Thumbnail ${thumbnailStartIdx + idx + 1}`}
                    className="w-full h-full object-cover"
                    onError={e => { e.target.style.background = '#eee'; e.target.src = '/fallback.png'; }}
                  />
                </button>
              ))}
            </div>
            {thumbnailStartIdx + 5 < images.length && (
              <button
                onClick={() => setThumbnailStartIdx(prev => Math.min(images.length - 5, prev + 1))}
                className="p-2 rounded-full bg-white dark:bg-zinc-800 border border-gray-300 dark:border-zinc-700 hover:bg-gray-100 dark:hover:bg-zinc-700"
                aria-label="Nächste Thumbnails"
              >
                ›
              </button>
            )}
          </div>
        </div>
      )}

      {/* Klassischer Zurück-Button */}
      <div className="flex justify-center mb-6 md:mb-8">
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 rounded-full bg-white dark:bg-zinc-800 border border-gray-300 dark:border-zinc-700 shadow hover:bg-gray-100 dark:hover:bg-zinc-700 text-gray-700 dark:text-gray-200 font-semibold"
        >
          ← {t('gallery.back')}
        </button>
      </div>

      {/* Details Card */}
      <section className="flex justify-center w-full px-1 sm:px-2 md:px-12 lg:px-24">
        <div className="w-full max-w-full md:max-w-4xl bg-white/90 dark:bg-zinc-800/90 rounded-xl shadow-lg p-3 md:p-8 mt-2 mb-4 md:mt-4 md:mb-12">
          
          {/* 1. Name des Gemäldes - Prominenter */}
          <div className="text-center mb-6 md:mb-8">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-2">
              {painting.name}
            </h1>
          </div>

          {/* 2. Beschreibung */}
          {(painting.description_de || painting.description_en) && (
            <div className="text-center mb-6 md:mb-8">
              <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 whitespace-pre-line leading-relaxed max-w-3xl mx-auto">
                {i18n.language === 'de' ? painting.description_de : painting.description_en}
              </p>
            </div>
          )}

          {/* 3. Jahr */}
          {painting.year && (
            <div className="flex flex-col items-center text-center mb-6 md:mb-8">
              <span className="text-sm md:text-base uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-2 font-medium">{t('painting.year')}</span>
              <span className="font-bold text-2xl md:text-3xl lg:text-4xl text-gray-900 dark:text-white">{painting.year}</span>
            </div>
          )}

          {/* 4. Größe */}
          {painting.size && (
            <div className="flex flex-col items-center text-center mb-8 md:mb-10">
              <span className="text-sm md:text-base uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-2 font-medium">{t('painting.size') + ' ' + t('painting.dimensions_format')}</span>
              <span className="font-bold text-2xl md:text-3xl lg:text-4xl text-gray-900 dark:text-white">{painting.size}</span>
            </div>
          )}

          {/* 5. Ausstellungen - Besonders hervorgehoben für Käufer/Sammler */}
          {exhibitionObjs.length > 0 && (
            <div className="mb-8 md:mb-12">
              <h2 className="text-xl md:text-3xl font-bold mb-6 text-gray-800 dark:text-gray-100 text-center flex items-center gap-3 justify-center">
                <div className="w-12 h-1 bg-blue-500 rounded-full"></div>
                {t('painting.exhibitions')}
                <div className="w-12 h-1 bg-blue-500 rounded-full"></div>
              </h2>
              
              {/* Kompakte Badge-Style Darstellung */}
              <div className="max-w-4xl mx-auto">
                {exhibitionObjs.map((e, index) => (
                  <Link
                    key={e.id}
                    to={`/exhibitions/${e.id}`}
                    className="group block mb-3 last:mb-0"
                  >
                    <div className="relative overflow-hidden rounded-lg bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 transition-all duration-300 hover:shadow-lg hover:border-blue-400 dark:hover:border-blue-600">
                      {/* Symbol */}
                      <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center">
                        <HiOutlineBuildingLibrary className="text-2xl text-white" />
                      </div>
                      
                      {/* Content */}
                      <div className="pl-20 pr-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {e.name}
                        </h3>
                        <div className="flex flex-col items-end gap-1 text-sm text-gray-600 dark:text-gray-400">
                          <span className="inline-flex items-center gap-1.5">
                            <HiOutlineCalendar className="text-blue-500" />
                            {formatDate(e.start_date)} – {formatDate(e.end_date)}
                          </span>
                          {(e.location || (Array.isArray(e.locations) && e.locations.length > 0) || (typeof e.locations === 'string' && e.locations.trim() !== '')) && (
                            <span className="inline-flex items-center gap-1.5">
                              <HiOutlineBuildingOffice2 className="text-blue-500" />
                              {[
                                e.location,
                                ...(Array.isArray(e.locations)
                                  ? e.locations
                                  : (typeof e.locations === 'string' && e.locations.trim() !== ''
                                    ? [e.locations]
                                    : []
                                  )
                                )
                              ].filter(Boolean).join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Hover accent */}
                      <div className="absolute inset-y-0 right-0 w-1 bg-blue-500 transform translate-x-full transition-transform duration-300 group-hover:translate-x-0"></div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Farben - Auskommentiert da nicht mehr in den Infos gewünscht */}

          {/* 6. Awards */}
          {awardObjs.length > 0 && (
            <div className="mb-6 md:mb-8">
              <h3 className="text-lg md:text-2xl font-bold mb-6 text-gray-800 dark:text-gray-100 text-center flex items-center gap-3 justify-center">
                <div className="w-12 h-1 bg-yellow-500 rounded-full"></div>
                {t('painting.awards')}
                <div className="w-12 h-1 bg-yellow-500 rounded-full"></div>
              </h3>
              
              {/* Kompakte Badge-Style Darstellung */}
              <div className="max-w-4xl mx-auto">
                {awardObjs.map((a, index) => (
                  <Link
                    key={a.id}
                    to={`/awards/${a.id}`}
                    className="group block mb-3 last:mb-0"
                  >
                    <div className="relative overflow-hidden rounded-lg bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 transition-all duration-300 hover:shadow-lg hover:border-yellow-400 dark:hover:border-yellow-600">
                      {/* Symbol */}
                      <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-yellow-500 to-yellow-600 flex items-center justify-center">
                        <HiOutlineTrophy className="text-2xl text-white" />
                      </div>
                      
                      {/* Content */}
                      <div className="pl-20 pr-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-yellow-600 dark:group-hover:text-yellow-400 transition-colors">
                          {a.name}
                        </h3>
                        <div className="flex flex-col items-end gap-1 text-sm text-gray-600 dark:text-gray-400">
                          <span className="inline-flex items-center gap-1.5">
                            <HiOutlineCalendar className="text-yellow-500" />
                            {a.date ? formatMonthYear(a.date) : a.year}
                          </span>
                          {a.location && (
                            <span className="inline-flex items-center gap-1.5">
                              <HiOutlineBuildingOffice2 className="text-yellow-500" />
                              {a.location}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Hover accent */}
                      <div className="absolute inset-y-0 right-0 w-1 bg-yellow-500 transform translate-x-full transition-transform duration-300 group-hover:translate-x-0"></div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* 7. Serie */}
          {painting.serie_id && serieObj && (
            <div className="mb-6 md:mb-8">
              <h3 className="text-lg md:text-xl font-medium mb-4 text-gray-600 dark:text-gray-400 text-center">
                {t('painting.series')}
              </h3>
              <div className="flex justify-center">
                <Link
                  to={`/series/${painting.serie_id}`}
                  className="group inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-violet-100 to-purple-100 dark:from-violet-900/30 dark:to-purple-900/30 border border-violet-200 dark:border-violet-700 rounded-full hover:shadow-md hover:scale-105 transition-all duration-200"
                >
                  <HiOutlineSquares2X2 className="text-violet-600 dark:text-violet-400 text-lg group-hover:rotate-12 transition-transform duration-200" />
                  <span className="font-medium text-violet-800 dark:text-violet-200 group-hover:text-violet-900 dark:group-hover:text-violet-100">
                    {serieObj.name}
                  </span>
                </Link>
              </div>
            </div>
          )}

        </div>
      </section>

      {/* Modal für Fullscreen-Bild */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90" onClick={() => setIsModalOpen(false)}>
          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src={`${API_URL}${images[imgIdx].file_path}`}
              alt={painting.name}
              className="max-w-full max-h-full object-contain"
            />
            <button
              className="absolute top-4 right-4 p-2 rounded border border-gray-300 dark:border-zinc-700 bg-white/80 dark:bg-zinc-800/80 shadow text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-400 z-[10000] text-2xl font-bold"
              onClick={e => { e.stopPropagation(); setIsModalOpen(false); }}
              aria-label="Schließen"
            >
              ✕
            </button>
            {/* Prev Button */}
            {images.length > 1 && (
              <button
                className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 text-3xl text-white bg-black/40 rounded-full px-2 py-1 z-50 hover:bg-black/70"
                onClick={e => { e.stopPropagation(); setImgIdx(idx => (idx - 1 + images.length) % images.length); }}
                aria-label="Vorheriges Bild"
              >
                ‹
              </button>
            )}
            {/* Next Button */}
            {images.length > 1 && (
              <button
                className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 text-3xl text-white bg-black/40 rounded-full px-2 py-1 z-50 hover:bg-black/70"
                onClick={e => { e.stopPropagation(); setImgIdx(idx => (idx + 1) % images.length); }}
                aria-label="Nächstes Bild"
              >
                ›
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PaintingDetail 